import * as T from "three";
import { Component } from "./live-codec.js";
import { activityNames } from "./player-activities.js";

export class ClipCache {
    constructor(rpc, diagnostic = () => {}, { concurrency = 6, maxBytes = 256 * 1024 * 1024, maxEntries = 1024, now = () => performance.now() } = {}) {
        Object.assign(this, { rpc, diagnostic, concurrency, maxBytes, maxEntries, now });
        this.entries = new Map(); this.running = 0; this.bytes = 0; this.closed = false;
    }
    get(reference) {
        if (this.closed) return null;
        const key = `${reference.path}:${reference.index}`;
        let entry = this.entries.get(key);
        if (!entry) {
            if (this.entries.size >= this.maxEntries && !this.evict()) return null;
            entry = { reference, pending: true, size: 0 }; this.entries.set(key, entry); this.pump();
        } else {
            this.entries.delete(key); this.entries.set(key, entry);
            if (entry.retryAt != null && this.now() >= entry.retryAt && !entry.loading) { entry.retryAt = null; entry.pending = true; this.pump(); }
        }
        return entry.clip ?? null;
    }
    evict() {
        // A resolved clip can be evicted even before its promise's finally
        // callback runs; simultaneous completions must still honor maxBytes.
        for (const [key, entry] of this.entries) if (entry.clip || !entry.pending && !entry.loading) {
            this.entries.delete(key); this.bytes -= entry.size; return true;
        }
        return false;
    }
    pump() {
        for (const entry of this.entries.values()) {
            if (this.closed || this.running >= this.concurrency) break;
            if (!entry.pending) continue;
            entry.pending = false; entry.loading = true; this.running++;
            Promise.resolve().then(() => this.rpc("animation", entry.reference)).then(({ clip }) => {
                if (this.closed) return;
                const size = clip.positions.byteLength + clip.quaternions.byteLength;
                if (size > this.maxBytes) throw new Error("Clip exceeds animation cache budget");
                while (this.bytes + size > this.maxBytes && this.evict()) { /* Evict completed least-recently-used clips. */ }
                entry.clip = clip; entry.size = size; this.bytes += size;
            }).catch(error => {
                if (!this.closed) { entry.retryAt = this.now() + 2000; this.diagnostic(`${entry.reference.path}: ${error.message}`); }
            }).finally(() => { this.running--; entry.loading = false; this.pump(); });
        }
    }
    dispose() { this.closed = true; this.entries.clear(); this.bytes = 0; }
}

export function sequenceBlend(sequence, poses = {}) {
    const axes = sequence.dimensions.map((size, axis) => {
        if (size <= 1) return [0, 0, 0];
        const descriptor = sequence.poses[sequence.params[axis]], value = poses[descriptor?.name] ?? descriptor?.start ?? 0;
        const keys = sequence.poseKeys?.[axis] ?? Array.from({ length: size }, (_, i) => sequence.starts[axis] + (sequence.ends[axis] - sequence.starts[axis]) * i / (size - 1));
        const ascending = keys.at(-1) >= keys[0], x = ascending ? value : -value, ordered = ascending ? keys : keys.map(v => -v);
        let lo = 0;
        while (lo < size - 2 && x > ordered[lo + 1]) lo++;
        const amount = T.MathUtils.clamp((x - ordered[lo]) / (ordered[lo + 1] - ordered[lo] || 1), 0, 1);
        return [lo, lo + 1, amount];
    });
    const result = new Map();
    for (let y = 0; y < 2; y++) for (let x = 0; x < 2; x++) {
        const weight = (x ? axes[0][2] : 1 - axes[0][2]) * (y ? axes[1][2] : 1 - axes[1][2]);
        if (!weight) continue;
        const reference = sequence.clips[axes[1][y] * sequence.dimensions[0] + axes[0][x]];
        if (!reference) continue;
        const key = `${reference.path}:${reference.index}`, old = result.get(key);
        result.set(key, { reference, weight: weight + (old?.weight ?? 0) });
    }
    return [...result.values()];
}

export function playerMotion(player, velocity) {
    const moving = Math.hypot(velocity[0], velocity[1]) > 10;
    if (player.state & 8) return "SWIM";
    if (!(player.state & 2)) return "JUMP_FLOAT";
    if (player.state & 4) return moving ? "CROUCHWALK" : "CROUCH_IDLE";
    return moving ? "RUN" : "STAND_IDLE";
}
export function weaponCategory(name = "", playerClass) {
    if (/knife|shovel|bat|bottle|bonesaw|wrench|fists|fireaxe|sword|club|saxxy|stickbomb|slap/.test(name)) return "MELEE";
    if (/pistol|medigun|smg|flaregun|jar|lunchbox|buff_item|invis|pipebomblauncher/.test(name)) return "SECONDARY";
    if (/shotgun/.test(name) && playerClass !== 9) return "SECONDARY";
    return playerClass === 8 ? "SECONDARY" : "PRIMARY";
}

// Scene layers are driven by CBaseFlex's scene clock (often playback rate 0).
// Interpolate captured cycles rather than starting them over when clips arrive.
export function animationLayersAt(history, time, sequences) {
    if (!history?.length) return [];
    let last = history[0], next;
    for (let i = 1; i < history.length; i++) {
        if (history[i].time > time) { next = history[i]; break; }
        last = history[i];
    }
    if (!next || time <= last.time) return last.layers;
    const amount = T.MathUtils.clamp((time - last.time) / (next.time - last.time), 0, 1);
    const before = new Map(last.layers.map(layer => [layer.slot, layer]));
    const after = new Map(next.layers.map(layer => [layer.slot, layer]));
    return [...new Set([...before.keys(), ...after.keys()])].map(slot => {
        const a = before.get(slot), b = after.get(slot);
        if (!b) return { ...a, weight: a.weight * (1 - amount) };
        if (!a || a.sequence !== b.sequence) return { ...b, weight: b.weight * amount };
        let end = b.cycle;
        if (sequences[b.sequence]?.flags & 1 && end < a.cycle) end += 1;
        return { ...b, cycle: sequences[b.sequence]?.flags & 1 ? T.MathUtils.lerp(a.cycle, end, amount) % 1 : T.MathUtils.lerp(a.cycle, end, amount),
            weight: T.MathUtils.lerp(a.weight, b.weight, amount) };
    });
}

export class AnimationPlayer {
    constructor(rig, getClip, diagnostic = () => {}) {
        this.rig = rig; this.getClip = getClip; this.diagnostic = diagnostic;
        this.particleTracks = [];
        this.library = rig.data.library; this.maps = new WeakMap(); this.weightMaps = new WeakMap(); this.warned = new Set();
        this.pose = rig.bones.map(() => ({ position: new T.Vector3(), quaternion: new T.Quaternion() }));
        this.from = this.pose.map(() => ({ position: new T.Vector3(), quaternion: new T.Quaternion() }));
        this.a = new T.Quaternion(); this.b = new T.Quaternion(); this.q = new T.Quaternion(); this.p = new T.Vector3();
        this.byActivity = new Map(); this.byName = new Map();
        for (const sequence of this.library.sequences) {
            if (sequence.activity) this.byActivity.set(sequence.activity.toUpperCase(), sequence);
            this.byName.set(sequence.name.toUpperCase(), sequence);
        }
        this.resetPose();
        this.hasPose = false; this.waitingForClips = false;
    }
    resetPose() { this.pose.forEach((pose, i) => { pose.position.fromArray(this.rig.data.bones[i].position); pose.quaternion.fromArray(this.rig.data.bones[i].quaternion); }); }
    warn(message) { if (!this.warned.has(message)) { this.warned.add(message); this.diagnostic(message); } }
    choosePlayer(player, velocity, weapon) {
        const motion = playerMotion(player, velocity), category = weaponCategory(weapon, player.playerClass);
        const loser = !!(player.state & 256);
        const competitiveLoser = !!((player.conditions?.[3] ?? 0) & (1 << (117 - 96)));
        const names = [competitiveLoser && motion === "STAND_IDLE" ? "ACT_MP_COMPETITIVE_LOSERSTATE" : null,
            loser ? `ACT_MP_${motion === "CROUCH_IDLE" ? "CROUCH" : motion}_LOSERSTATE` : null, `ACT_MP_${motion}_${category}`, `ACT_MP_${motion}`, `${motion}_${category}`, motion];
        if (loser && motion === "JUMP_FLOAT") names.splice(1, 0, "ACT_MP_JUMP_START_LOSERSTATE", "ACT_MP_JUMP_LOSERSTATE");
        if (motion === "JUMP_FLOAT") names.push(`ACT_MP_JUMP_START_${category}`, `JUMP_${category}`, "JUMP");
        if (motion === "CROUCH_IDLE") names.push(`ACT_MP_CROUCH_${category}`, `CROUCH_${category}`);
        if (motion === "STAND_IDLE") names.push(`ACT_MP_STAND_${category}`, `STAND_${category}`, `IDLE_${category}`, "IDLE");
        for (const name of names) { const sequence = this.byActivity.get(name) ?? this.byName.get(name); if (sequence?.clips.length) return sequence; }
        return null;
    }
    findActivity(name, category) {
        if (!name) return null;
        const attack = /^ACT_MP_ATTACK_(STAND|CROUCH|SWIM)_(?:PRIMARY|SECONDARY|MELEE)FIRE$/.exec(name);
        const candidates = [attack && `ACT_MP_ATTACK_${attack[1]}_${category}`, category && `${name}_${category}`, name];
        return candidates.filter(Boolean).map(n => this.byActivity.get(n) ?? this.byName.get(n)).find(s => s?.clips.length) ?? null;
    }
    serverEvent(state, time, weapon) {
        const event = state.components[Component.PlayerAnimation], player = state.components[Component.Player];
        if (!event || !player || time < event.startTime || event.serial === this.serverSerial) return;
        this.serverSerial = event.serial;
        const category = weaponCategory(weapon, player.playerClass), posture = player.state & 8 ? "SWIM" : player.state & 4 ? "CROUCH" : "STAND";
        if ([16, 17].includes(event.event)) { this.serverMain = this.serverGesture = this.activeGesture = null; this.sequence = null; return; }
        let sequence = null, main = event.event === 19 || event.event === 21;
        if ([21, 22].includes(event.event)) sequence = this.library.reliable ? this.library.sequences[event.data] : null;
        else if ([19, 20, 29].includes(event.event)) sequence = this.findActivity(activityNames.get(event.data), category);
        else {
            let name;
            if (event.event === 0) name = `ACT_MP_ATTACK_${posture}_${category}FIRE`;
            else if (event.event === 1) name = `ACT_MP_ATTACK_${posture}_SECONDARYFIRE`;
            else if ([3, 4, 5].includes(event.event)) name = `ACT_MP_RELOAD_${posture}_${category}${event.event === 4 ? "_LOOP" : event.event === 5 ? "_END" : ""}`;
            else if (event.event >= 9 && event.event <= 14) name = `ACT_MP_GESTURE_FLINCH_${["CHEST", "HEAD", "LEFTARM", "RIGHTARM", "LEFTLEG", "RIGHTLEG"][event.event - 9]}`;
            else if (event.event === 23 || event.event === 24) name = `ACT_MP_ATTACK_${posture}_${event.event === 23 ? "PREFIRE" : "POSTFIRE"}`;
            else if (event.event >= 31 && event.event <= 33) name = `ACT_MP_STUN_${["BEGIN", "MIDDLE", "END"][event.event - 31]}`;
            sequence = this.findActivity(name, category);
        }
        if (!sequence?.clips.length) return;
        const gesture = { sequence, started: event.startTime, serial: event.serial };
        if (main) this.serverMain = gesture;
        else this.serverGesture = gesture;
    }
    eventCycle(event, time) {
        const clip = event.sequence.clips[0], duration = Math.max(.001, (clip.frames - 1) / Math.max(.001, clip.fps));
        return (time - event.started) / 1000 / duration;
    }
    poses(state) {
        const values = {}, normalized = state.components[Component.Animation]?.poses ?? [];
        this.library.poses.forEach((pose, i) => values[pose.name] = pose.start + (pose.end - pose.start) * (normalized[i] ?? 0));
        const player = state.components[Component.Player];
        // TF2 authors taunt movement/turn poses on the server; locomotion
        // normalization would overwrite the VCD movement blend parameters.
        if (player && !((player.conditions?.[0] ?? 0) & 128)) {
            const transform = state.components[Component.Transform], yaw = transform.angles[1] * Math.PI / 180;
            const [x, y] = transform.velocity, speed = Math.max(1, Math.hypot(x, y));
            // CMultiPlayerAnimState::ComputePoseParam_AimPitch uses -eyePitch.
            // Source eye pitch is positive down; the studio aim pose is positive up.
            Object.assign(values, { body_pitch: -(((player.eyeAngles[0] + 180) % 360) - 180),
                body_yaw: ((player.eyeAngles[1] - transform.angles[1] + 540) % 360) - 180,
                move_x: (x * Math.cos(yaw) + y * Math.sin(yaw)) / speed,
                // CMultiPlayerAnimState::ComputePoseParam_MoveYaw uses -sin
                // of movement yaw relative to the body for studio move_y.
                move_y: (x * Math.sin(yaw) - y * Math.cos(yaw)) / speed });
        }
        return values;
    }
    sample(sequence, cycle, values, overlay = false, strength = 1, depth = 0, channel = "base", reset = undefined) {
        this.particleTracks.push({ sequence, cycle, weight: strength, depth, channel, reset,
            cycleRate: channel === "base" ? this.particleCycleRate : undefined,
            loop: channel === "base" && this.particleLoop !== undefined ? this.particleLoop : !!(sequence.flags & 1) });
        const blends = sequenceBlend(sequence, values).map(item => ({ ...item, clip: this.getClip(item.reference) }));
        if (!blends.length || blends.some(item => !item.clip)) return false;
        // Clip remapping and frame addresses are shared by every bone in this
        // sample. Resolve them once, including when layers recursively sample.
        for (const item of blends) {
            const { clip } = item;
            let map = this.maps.get(clip);
            if (!map) {
                const names = new Map(clip.boneNames.map((name, index) => [name.toLowerCase(), index]));
                map = this.rig.bones.map(bone => names.get(bone.name.toLowerCase()) ?? -1);
                this.maps.set(clip, map);
            }
            const position = T.MathUtils.clamp(cycle, 0, 1) * (clip.frames - 1), first = Math.floor(position);
            item.map = map; item.first = first * clip.boneNames.length;
            item.next = Math.min(clip.frames - 1, first + 1) * clip.boneNames.length;
            item.fraction = position - first;
        }
        let weights = this.weightMaps.get(sequence);
        if (!weights) {
            const names = new Map(sequence.boneNames.map((name, index) => [name.toLowerCase(), index]));
            weights = this.rig.bones.map(bone => {
                const index = names.get(bone.name.toLowerCase());
                return index === undefined ? 0 : T.MathUtils.clamp(sequence.weights[index], 0, 1);
            });
            this.weightMaps.set(sequence, weights);
        }
        for (let boneIndex = 0; boneIndex < this.pose.length; boneIndex++) {
            if (!weights[boneIndex]) continue;
            let sum = 0, x = 0, y = 0, z = 0;
            for (const { clip, weight, map, first, next, fraction } of blends) {
                const index = map[boneIndex]; if (index < 0) continue;
                const a = first + index, b = next + index;
                x += weight * T.MathUtils.lerp(clip.positions[a * 3], clip.positions[b * 3], fraction);
                y += weight * T.MathUtils.lerp(clip.positions[a * 3 + 1], clip.positions[b * 3 + 1], fraction);
                z += weight * T.MathUtils.lerp(clip.positions[a * 3 + 2], clip.positions[b * 3 + 2], fraction);
                this.a.fromArray(clip.quaternions, a * 4);
                if (fraction) { this.b.fromArray(clip.quaternions, b * 4); this.a.slerp(this.b, fraction); }
                if (!sum) this.q.copy(this.a);
                else this.q.slerp(this.a, weight / (sum + weight));
                sum += weight;
            }
            if (!sum) continue;
            this.p.set(x / sum, y / sum, z / sum);
            const target = this.pose[boneIndex], weight = weights[boneIndex] * strength;
            if (sequence.flags & 4) {
                target.position.addScaledVector(this.p, weight); this.a.identity().slerp(this.q, weight); if (sequence.flags & 16) target.quaternion.multiply(this.a); else target.quaternion.premultiply(this.a);
            } else if (weight === 1) { target.position.copy(this.p); target.quaternion.copy(this.q); }
            else { target.position.lerp(this.p, weight); target.quaternion.slerp(this.q, weight); }
        }
        if (depth < 4) for (const [layerSlot, layer] of (sequence.layers ?? []).entries()) {
            const child = this.byName.get(layer.name.toUpperCase());
            if (!child?.clips.length || child === sequence) continue;
            let weight = strength, layerCycle = cycle;
            if (layer.start !== layer.end) {
                const descriptor = child.poses?.[layer.pose];
                const index = layer.flags & 0x4000 ? values[descriptor?.name] ?? 0 : cycle;
                if (index < layer.start || index >= layer.end) continue;
                let amount = index < layer.peak ? (index - layer.start) / (layer.peak - layer.start)
                    : index > layer.tail ? (layer.end - index) / (layer.end - layer.tail) : 1;
                if (layer.flags & 0x40) amount = amount * amount * (3 - 2 * amount);
                weight = layer.flags & 0x80 && index > layer.tail ? amount * strength / (1 - strength + amount * strength || 1)
                    : layer.flags & 0x200 ? amount : strength * amount;
                if (!(layer.flags & 0x4000)) layerCycle = (cycle - layer.start) / (layer.end - layer.start);
            }
            this.sample(child, layerCycle, values, true, weight, depth + 1, `${channel}/auto:${layerSlot}:${layer.index}`, reset);
        }
        return true;
    }
    gesture(state, time, weapon) {
        const player = state.components[Component.Player], signal = player.state & 48;
        const category = weaponCategory(weapon, player.playerClass), posture = player.state & 4 ? "CROUCH" : "STAND";
        if (signal && (signal !== this.gestureSignal || category !== this.gestureCategory)) {
            const action = signal & 32 ? "RELOAD" : "ATTACK";
            const names = [`ACT_MP_${action}_${posture}_${category}${action === "ATTACK" ? "FIRE" : ""}`,
                `ACT_MP_${action}_${posture}_${category}`, `${action}_${category}`, `GESTURE_${category}_${action}`];
            const sequence = names.map(name => this.byActivity.get(name) ?? this.byName.get(name)).find(value => value?.clips.length);
            this.activeGesture = sequence ? { sequence, started: time } : null;
        }
        this.gestureSignal = signal; this.gestureCategory = category;
        if (!this.activeGesture) return;
        const { sequence, started } = this.activeGesture, clip = sequence.clips[0];
        const duration = Math.max(0.001, (clip.frames - 1) / Math.max(0.001, clip.fps));
        const cycle = (time - started) / 1000 / duration;
        if (cycle > 1) {
            if (signal & 16 && !(signal & 32)) this.activeGesture.started = time;
            else this.activeGesture = null;
            return;
        }
        this.sample(sequence, Math.max(0, cycle), this.poses(state), true, 1, 0, "gesture", started);
    }
    sceneLayers(layers, values) {
        let sampled = false;
        for (const layer of layers.toSorted((a, b) => a.slot - b.slot)) {
            const overlay = this.library.sequences[layer.sequence];
            if (overlay?.clips.length && layer.weight > 0) sampled = this.sample(overlay, layer.cycle, values, true, layer.weight, 0, `scene:${layer.slot}`) || sampled;
        }
        return sampled;
    }
    update(state, time, weapon = "") {
        this.particleTracks = []; this.particleTime = time;
        this.waitingForClips = false;
        const animation = state.components[Component.Animation], player = state.components[Component.Player];
        this.particleCycleRate = undefined; this.particleLoop = undefined;
        const generic = this.library.reliable && animation ? this.library.sequences[animation.sequence] : null;
        if (player) this.serverEvent(state, time, weapon);
        const taunting = !!((player?.conditions?.[0] ?? 0) & 128);
        const captured = state.components[Component.AnimationLayers];
        const sceneLayers = taunting && this.library.reliable && captured
            ? animationLayersAt(state.animationLayerHistory ?? [{ time: captured.time ?? state.sampleTime, layers: captured.layers }], time, this.library.sequences) : [];
        const hasScene = sceneLayers.some(layer => layer.weight > 0 && this.library.sequences[layer.sequence]?.clips.length);
        if (this.serverMain && (!taunting && this.eventCycle(this.serverMain, time) >= 1 || !(player.state & 1))) this.serverMain = null;
        // Taunts are server-selected sequences. Locomotion synthesis would hide
        // both base taunts and item-specific custom sequences sent by the server.
        const sequence = player ? (hasScene ? null : this.serverMain?.sequence) ?? (taunting && !hasScene ? generic : null) ?? this.choosePlayer(player, state.components[Component.Transform].velocity, weapon) ?? generic : generic;
        if (sequence === generic && (animation?.flags & 8)) {
            this.particleCycleRate = sequence?.flags & 0x80 ? undefined : animation.cycleRate;
            this.particleLoop = !!(animation.flags & 16);
        }
        if (!sequence?.clips.length) {
            this.resetPose();
            if (hasScene && !this.sceneLayers(sceneLayers, this.poses(state))) {
                this.waitingForClips = !this.hasPose; return;
            }
            if (!hasScene && this.hasPose) { this.warn("Model animation unavailable; retaining last pose"); return; }
            if (hasScene) this.hasPose = true;
            this.waitingForClips = !!player && !this.hasPose;
            this.rig.bones.forEach((bone, i) => { bone.position.copy(this.pose[i].position); bone.quaternion.copy(this.pose[i].quaternion); });
            if (!hasScene) this.warn(player ? "Model animation unavailable; retaining current pose" : "Model animation unavailable; displaying bind pose");
            return;
        }
        if (this.sequence !== sequence) {
            const motion = player && playerMotion(player, state.components[Component.Transform].velocity);
            const old = this.sequence?.clips[0];
            const phase = old && motion === this.motion ? ((time - this.started) / 1000 * old.fps / Math.max(1, old.frames - 1)) % 1 : 0;
            this.from.forEach((pose, i) => { pose.position.copy(this.rig.bones[i].position); pose.quaternion.copy(this.rig.bones[i].quaternion); });
            this.sequence = sequence; this.motion = motion; this.transitionAt = time;
            const next = sequence.clips[0];
            this.started = time - phase * Math.max(1, next.frames - 1) / Math.max(0.001, next.fps) * 1000;
        }
        const clip = sequence.clips[0], duration = Math.max(0.001, (clip.frames - 1) / Math.max(0.001, clip.fps));
        let cycle = player ? (time - this.started) / 1000 / duration
            : (animation?.cycle ?? 0) + (time - (state.animationTime ?? state.sampleTime)) / 1000 * (animation?.rate ?? 1) / duration;
        if (this.serverMain?.sequence === sequence) cycle = this.eventCycle(this.serverMain, time);
        else if (player && taunting && sequence === generic) cycle = (animation?.cycle ?? 0) + (time - (state.animationTime ?? state.sampleTime)) / 1000 * (animation?.rate ?? 1) / duration;
        if ((animation?.flags & 4) && sequence === generic)
            cycle = animation.startCycle + Math.max(0, time - animation.startTime) / 1000 * animation.rate / duration;
        if ((animation?.flags & 8) && sequence === generic)
            cycle = animation.cycle + Math.max(0, time - animation.anchorTime) / 1000 * animation.cycleRate;
        const loop = animation?.flags & 8 && sequence === generic ? animation.flags & 16 : sequence.flags & 1;
        cycle = loop ? ((cycle % 1) + 1) % 1 : T.MathUtils.clamp(cycle, 0, 1);
        const values = this.poses(state);
        if (sequence.flags & 0x80) {
            const pose = sequence.poses[sequence.cyclePose];
            if (pose) cycle = T.MathUtils.clamp(((values[pose.name] ?? pose.start) - pose.start) / (pose.end - pose.start || 1), 0, 1);
        }
        this.resetPose();
        const sampledBase = this.sample(sequence, cycle, values);
        if (!sampledBase) {
            // TF2 player bind poses use authoring axes and can lie along the
            // ground. A cache miss must not replace a valid game pose with one.
            if (!hasScene) { this.waitingForClips = !this.hasPose; return; }
            if (this.hasPose) this.pose.forEach((pose, i) => {
                pose.position.copy(this.rig.bones[i].position); pose.quaternion.copy(this.rig.bones[i].quaternion);
            });
        }
        if (player && !taunting && !(player.state & 256)) {
            const gestureCycle = this.serverGesture && this.eventCycle(this.serverGesture, time);
            if (this.serverGesture && gestureCycle <= 1) this.sample(this.serverGesture.sequence, Math.max(0, gestureCycle), values, true, 1, 0, "gesture", `${this.serverGesture.serial}:${this.serverGesture.started}`);
            else { this.serverGesture = null; this.gesture(state, time, weapon); }
        }
        // A VCD taunt overlays the player's base pose. Its native sequence IDs,
        // cycles and weights also recover taunts that began before viewer join.
        const sampledScene = this.sceneLayers(sceneLayers, values);
        if (!sampledBase && !sampledScene) { this.waitingForClips = !this.hasPose; return; }
        // The first loaded pose has no valid predecessor to cross-fade from.
        if (!this.hasPose) this.transitionAt = time - 150;
        const blend = T.MathUtils.clamp((time - this.transitionAt) / 150, 0, 1);
        this.rig.bones.forEach((bone, i) => {
            if (blend === 1) { bone.position.copy(this.pose[i].position); bone.quaternion.copy(this.pose[i].quaternion); }
            else {
                bone.position.lerpVectors(this.from[i].position, this.pose[i].position, blend);
                bone.quaternion.slerpQuaternions(this.from[i].quaternion, this.pose[i].quaternion, blend);
            }
        });
        this.hasPose = true;
    }
}
